"use client";

import { useTranslations } from "next-intl";
import { ArrowLeftRight, Check, File } from "lucide-react";
import { cn } from "@/lib/utils";
import { contractBar } from "./heroTimeline";

export const CONTRACT_STAGES = ["draft", "review", "signed", "active"] as const;

const REVIEW_STAGE = 1;
const APPROVED_STAGE = 2;

interface ContractStageCardProps {
  stage: number;
  progress: number;
  showChips: boolean;
  motion: boolean;
  onSelectStage: (stage: number) => void;
}

export default function ContractStageCard({
  stage,
  progress,
  showChips,
  motion,
  onSelectStage,
}: ContractStageCardProps) {
  const t = useTranslations("home.contractCard");
  const periodFill = contractBar(stage, progress);

  return (
    <article
      data-phase={motion ? "play" : "idle"}
      className="hero-slide relative w-full max-w-[540px] flex-1 rounded-[24px] bg-brand-navy p-6 text-brand-cream shadow-[14px_14px_0_rgba(15,27,45,0.14)] sm:p-8"
      aria-label={t("title")}
    >
      {showChips && stage === REVIEW_STAGE && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-4 right-4 z-10 animate-hero-float motion-reduce:animate-none sm:-right-8"
        >
          <div className="flex animate-hero-pop items-center gap-2 rounded-full bg-brand-cream px-4 py-2 text-sm font-semibold text-brand-navy shadow-[4px_4px_0_var(--brand-navy)] motion-reduce:animate-none">
            <span className="h-2 w-2 animate-home-blink rounded-full bg-brand-accent motion-reduce:animate-none" />
            {t("chipReview")}
          </div>
        </div>
      )}

      {showChips && stage === APPROVED_STAGE && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-4 left-4 z-10 animate-hero-float motion-reduce:animate-none sm:-left-8"
        >
          <div className="flex animate-hero-pop items-center gap-2 rounded-full bg-brand-accent px-4 py-2 text-sm font-bold text-brand-navy shadow-[4px_4px_0_var(--brand-navy)] motion-reduce:animate-none">
            <Check className="h-4 w-4" strokeWidth={3} />
            {t("chipApproved")}
          </div>
        </div>
      )}

      <header className="mb-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-border-dark bg-brand-navy-2 px-3 py-1 text-sm font-semibold">
            <File className="h-3.5 w-3.5 text-brand-accent" aria-hidden="true" />
            {t("number")}
          </span>
          <span className="flex items-center gap-2 text-sm text-brand-cream">
            <span className="rounded-full border border-brand-border-dark px-3 py-1">{t("yourCompany")}</span>
            <ArrowLeftRight
              className={cn(
                "step-node h-3.5 w-3.5",
                stage >= APPROVED_STAGE ? "text-brand-accent" : "text-brand-muted-dark",
              )}
              aria-hidden="true"
            />
            <span className="rounded-full border border-brand-border-dark px-3 py-1">{t("organization")}</span>
          </span>
        </div>
        <h2 className="min-h-[55px] text-[22px] font-semibold leading-snug">{t("title")}</h2>
      </header>

      <ol className="mb-6">
        {CONTRACT_STAGES.map((key, index) => {
          const isDone = index < stage;
          const isActive = index === stage;
          const fill = index < stage ? 1 : isActive ? progress : 0;
          const isLast = index === CONTRACT_STAGES.length - 1;

          return (
            <li key={key} className={cn("relative", !isLast && "pb-4")}>
              {!isLast && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-[17px] top-10 w-0.5 overflow-hidden rounded-full bg-brand-track"
                >
                  <span
                    className="block h-full w-full origin-top bg-brand-accent"
                    style={{ transform: `scaleY(${fill})` }}
                  />
                </span>
              )}
              <button
                type="button"
                onClick={() => onSelectStage(index)}
                aria-current={isActive ? "step" : undefined}
                className="flex w-full items-start gap-4 rounded-xl p-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy"
              >
                <span
                  className={cn(
                    "step-node relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold",
                    isDone && "border-brand-accent bg-brand-accent text-brand-navy",
                    isActive && "border-brand-cream bg-brand-cream text-brand-navy",
                    !isDone && !isActive && "border-brand-border-dark bg-transparent text-brand-muted-dark",
                  )}
                >
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-[-2px] animate-hero-ring rounded-full border-2 border-brand-cream motion-reduce:animate-none"
                    />
                  )}
                  {isDone ? <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> : index + 1}
                </span>
                <span className="min-w-0 flex-1 pt-1">
                  <span
                    className={cn(
                      "step-label block text-base font-semibold",
                      isActive || isDone ? "text-brand-cream" : "text-brand-muted-dark",
                    )}
                  >
                    {t(`stages.${key}.title`)}
                  </span>
                  <span
                    className={cn(
                      "block overflow-hidden text-sm text-brand-muted-dark",
                      isActive ? "h-12 pt-1" : "h-0",
                    )}
                    aria-hidden={!isActive}
                  >
                    <span className="block pt-1">{t(`stages.${key}.description`)}</span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <footer className="border-t border-brand-border-dark pt-5">
        <div className="mb-1 grid grid-cols-[auto_1fr_auto] items-end gap-3 text-sm text-brand-muted-dark">
          <span>{t("startLabel")}</span>
          <span className="text-center">{t("periodLabel")}</span>
          <span className="text-right">{t("endLabel")}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold tabular-nums">{t("startDate")}</span>
          <div
            className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-brand-track"
            role="progressbar"
            aria-label={t("periodLabel")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(periodFill * 100)}
          >
            <div
              className="h-full w-full origin-left rounded-full bg-brand-accent"
              style={{ transform: `scaleX(${periodFill})` }}
            />
          </div>
          <span className="text-lg font-bold tabular-nums">{t("endDate")}</span>
        </div>
      </footer>
    </article>
  );
}
